/**
 * Show a vertically and horizontally centered message. A single component for
 * 404 pages, redirection notices, "You don't have access" pages, etc etc. Just
 * short, quick notices shown with a consistent layout and GDS-sanctioned
 * colors.
 */

import clsx from "clsx";
import { type GDSThemeColor } from "@granular/fabric3-definitions";
import { GKIcon, GKIconName } from "@granular/gds";

import styles from "./BigCenterMessage.module.css";

/**
 * Shows a big, centered message with a theme color and an icon of your
 * choosing in a consistent manner across the Monorepo/Container.
 *
 * Here's what I mean by a "theme color":
 * https://design.granular.ag/v2/design-system/foundations/Colors
 *
 * And here's a list of icons (don't worry, the `icon` property is typed)
 * https://design.granular.ag/v2/design-system/components/Icon
 *
 * Note that at least as of now, we're always showing an icon above the
 * heading. If you do not specify one, we'll just show the Granular logo.
 */
const Component: React.FC<{
  icon?: keyof typeof GKIconName | null;
  heading?: string | React.ReactNode;
  children?: React.ReactNode;

  /**
   * Whether or not to truncate the heading text (default is `false`). Useful
   * for when you want to show a long-ass URI in the heading (e.g. on the 404
   * page).
   */
  truncateHeadingText?: boolean;
  color?: GDSThemeColor;
  "data-qa"?: string;
}> = ({
  color = "light",
  children,
  heading,
  icon,
  truncateHeadingText = false,
  "data-qa": dataQa,
}) => (
  <div
    className={clsx(
      "align-items-center",
      `bg-${color}`,
      "d-flex",
      "h-100",
      "justify-content-center",
      `${styles.bigMessage}`,
    )}
    data-qa={dataQa}
  >
    <div
      className="m-3"
      data-message-color={color}
      data-qa="big-center-message-wrapper"
    >
      <div data-qa="big-center-message-icon">
        {icon ? (
          <GKIcon name={GKIconName[icon]} />
        ) : (
          <img
            src="https://static.granular.ag/gds/latest/assets/images/granular-logo.svg"
            alt="Granular Logo"
            className="mb-4"
          />
        )}
      </div>

      {heading && (
        <h1
          className={clsx("display-6", {
            "text-truncate": truncateHeadingText,
          })}
        >
          {heading}
        </h1>
      )}
      {children && <div>{children}</div>}
    </div>
  </div>
);

export default Component;
